import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import IconButton from 'components/@extended/IconButton';
import StatusChip from 'components/@extended/StatusChip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { CircleAlert, InfoOutlined, LockIcon, Trash2Icon } from 'components/icons';
import Loader from 'components/Loader';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useModal } from 'components/modals/useModal';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from 'components/ui/table';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { getInvoiceStatusChipType } from 'pages/invoices/util/invoiceUtil';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import {
    useDeleteInvoiceMutation,
    useGetPaymentDistributionQuery,
    useUpdatePaymentDistributionMutation,
} from 'store/api/invoices';
import {
    InvoiceStatusType,
    isNotSharedPaymentRefusal,
    PaymentDistributionCreditedInvoiceDto,
    PaymentDistributionDto,
    PaymentDistributionInvoiceDto,
} from 'types/invoices';
import { successAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import {
    formatShareRate,
    isShareRateInRange,
    isShareTotalComplete,
    shareAmountsFromRates,
    shareRateFromAmount,
    shareValueToNumber,
    sumShareAmounts,
    sumShareRates,
} from './paymentShare';
import {
    ACTIONS_CELL_CLASS,
    FOOTER_CELL_CLASS,
    NUMERIC_CELL_CLASS,
    NUMERIC_HEAD_CLASS,
    PAYER_TABLE_CLASS,
    PAYER_TABLE_FRAME_CLASS,
    READ_ONLY_LINK_CELL_CLASS,
    ShareTotalIndicator,
} from './PayerTableParts';
import { ShareInput, ShareRateDisplay } from './ShareInput';
import { MANAGE_INVOICE_PAYERS_PERMISSION } from './useSharedPayment';

interface ShareRow {
    invoiceId: number;
    shareRate: number | string;
    shareAmount: number | string;
}

interface DistributionForm {
    shares: ShareRow[];
}

interface DeletedInvoice {
    invoiceNumber: string;
    shareRate: number;
}

interface Props {
    /** The copy whose page the dialog is opened on. */
    invoiceId: number;
    onClose: () => void;
    /** The copy whose page is open was deleted; the page carries on with this copy of the group. */
    onOpenInvoiceDeleted?: (nextInvoiceId: number) => void;
}

export const PAYMENT_DISTRIBUTION_MODAL_STYLE = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    width: '960px',
    maxWidth: '95vw',
};

const INVOICE_LINK = 'text-link font-semibold no-underline hover:underline';
const CURRENT_INVOICE_ROW_CLASS = cn(
    SIMPLE_TABLE_ROW_CLASS,
    'bg-primary-surface-subtle hover:bg-primary-surface-subtle new:bg-primary-tint new:hover:bg-primary-tint'
);
const CREDITED_ROW_CLASS = cn(SIMPLE_TABLE_ROW_CLASS, '[&>td]:text-fg-secondary');

function mapDistributionToForm(distribution: PaymentDistributionDto | undefined): DistributionForm {
    return {
        shares: (distribution?.invoices ?? []).map((invoice) => ({
            invoiceId: invoice.id,
            shareRate: invoice.shareRate,
            shareAmount: invoice.shareAmount,
        })),
    };
}

export default function PaymentDistributionModal({ invoiceId, onClose, onOpenInvoiceDeleted }: Props) {
    const { openModal, closeModal } = useModal();
    const isNew = useIsNewDesign();
    const hasManagePermission = useHasPermissions([MANAGE_INVOICE_PAYERS_PERMISSION]);
    const { classifiers } = useClassifiers({ codes: ['INVOICE_STATUS_TYPE'] });
    const [deletedInvoice, setDeletedInvoice] = useState<DeletedInvoice | null>(null);
    // The dialog stays open when the copy it was opened on is deleted, and follows the page to a copy that is left.
    const [takeoverInvoiceId, setTakeoverInvoiceId] = useState<number | null>(null);
    const openInvoiceId = takeoverInvoiceId ?? invoiceId;
    const { currentData: distribution, error: distributionError } = useGetPaymentDistributionQuery(openInvoiceId);
    // A copy deleted elsewhere may have left this one the only invoice, which has nothing to distribute.
    const isGroupDissolved = isNotSharedPaymentRefusal(distributionError);
    const [updatePaymentDistribution, { isLoading: isSaving }] = useUpdatePaymentDistributionMutation();
    const [deleteInvoice, { isLoading: isDeleting }] = useDeleteInvoiceMutation();

    const { control, setValue, getValues, reset } = useForm<DistributionForm>({
        defaultValues: mapDistributionToForm(distribution),
    });
    const { fields } = useFieldArray({ control, name: 'shares', keyName: 'formKey' });
    const rows = useWatch({ control, name: 'shares' }) ?? [];

    useEffect(() => {
        reset(mapDistributionToForm(distribution));
    }, [distribution, reset]);

    const invoicesById = useMemo(
        () => new Map((distribution?.invoices ?? []).map((invoice) => [invoice.id, invoice])),
        [distribution]
    );
    const net = distribution?.fullTotalWithoutVat ?? 0;
    const matterNames = distribution?.matterNames?.length ? distribution.matterNames : [distribution?.matterName ?? ''];
    const currency = distribution?.currencyCode ?? '';
    const isLocked = distribution?.isLocked ?? false;
    const isEditable = !!distribution?.canEdit && hasManagePermission && !isLocked;
    const creditedInvoices = distribution?.creditedInvoices ?? [];
    const storedRates = (distribution?.invoices ?? []).map((invoice) => invoice.shareRate);
    const storedTotalRate = sumShareRates(storedRates);
    const isStoredTotalComplete = isShareTotalComplete(storedRates);

    const rates = rows.map((row) => row.shareRate);
    const amounts = rows.map((row) => shareValueToNumber(row.shareAmount));
    // A payable amount carries the payer's VAT, which the backend computes when the shares are saved: it is known
    // for a row at its saved share only.
    const payables = rows.map((row, index) => {
        const invoice = invoicesById.get(row.invoiceId);
        return invoice && amounts[index] === shareValueToNumber(invoice.shareAmount) ? invoice.payableAmount : null;
    });
    const totalRate = sumShareRates(rates);
    const isTotalComplete = isShareTotalComplete(rates) && rates.every(isShareRateInRange);
    const totalAmount = sumShareAmounts(amounts);
    const totalPayable = payables.every((payable) => payable !== null) ? sumShareAmounts(payables) : null;
    // Shown until the rows are brought back to 100 %, also when the dialog is reopened after a deletion.
    const showIncompleteAlert = !isLocked && (!!deletedInvoice || !isStoredTotalComplete) && !isTotalComplete;

    const handleRateChange = useCallback(
        (index: number, value: string) => {
            const nextRates = getValues('shares').map((row, rowIndex) => (rowIndex === index ? value : row.shareRate));
            shareAmountsFromRates(net, nextRates).forEach((amount, amountIndex) => {
                setValue(`shares.${amountIndex}.shareAmount`, amount);
            });
        },
        [getValues, net, setValue]
    );

    const handleAmountChange = useCallback(
        (index: number, value: string) => {
            setValue(`shares.${index}.shareRate`, shareRateFromAmount(net, value));
        },
        [net, setValue]
    );

    const handleSave = useCallback(() => {
        const shares = getValues('shares').map((row) => ({
            invoiceId: row.invoiceId,
            shareRate: shareValueToNumber(row.shareRate),
        }));
        updatePaymentDistribution({ id: openInvoiceId, shares })
            .unwrap()
            .then(() => {
                successAlert(t('invoices.payment-distribution.saved'));
                onClose();
            })
            .catch((error) => {
                if (isNotSharedPaymentRefusal(error)) {
                    onClose();
                }
                console.error(error);
            });
    }, [getValues, openInvoiceId, onClose, updatePaymentDistribution]);

    const handleDelete = useCallback(
        (invoice: PaymentDistributionInvoiceDto) => {
            const remainingInvoices = (distribution?.invoices ?? []).filter((item) => item.id !== invoice.id);
            const [lastRemainingInvoice] = remainingInvoices;
            const confirmation =
                remainingInvoices.length === 1
                    ? t('invoices.payment-distribution.delete-last-but-one-confirmation', {
                          number: invoice.invoiceNumber,
                          remaining: lastRemainingInvoice.invoiceNumber,
                      })
                    : t('invoices.payment-distribution.delete-confirmation', { number: invoice.invoiceNumber });
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    confirmId="delete"
                    content={<p className="text-body1">{confirmation}</p>}
                    alert={{
                        titleId: 'invoices.confirm.delete-invoice-title',
                        titleValues: { invoice: invoice.invoiceNumber },
                        description:
                            remainingInvoices.length === 1
                                ? t('invoices.payment-distribution.delete-last-but-one-text', {
                                      remaining: lastRemainingInvoice.invoiceNumber,
                                  })
                                : t('invoices.payment-distribution.delete-text'),
                        confirmId: 'invoices.confirm.delete-invoice',
                        tone: 'danger',
                    }}
                    onConfirm={() => {
                        const remainingInvoiceIds = remainingInvoices.map((item) => item.id);
                        deleteInvoice({ invoiceId: invoice.id, sharedPaymentInvoiceIds: remainingInvoiceIds })
                            .unwrap()
                            .then(() => {
                                const [nextInvoiceId] = remainingInvoiceIds;
                                if (invoice.id === openInvoiceId && nextInvoiceId !== undefined) {
                                    onOpenInvoiceDeleted?.(nextInvoiceId);
                                }
                                // The copy left is a regular invoice again, with nothing to distribute.
                                if (remainingInvoiceIds.length <= 1) {
                                    onClose();
                                    return;
                                }
                                setDeletedInvoice({
                                    invoiceNumber: invoice.invoiceNumber,
                                    shareRate: invoice.shareRate,
                                });
                                if (invoice.id === openInvoiceId) {
                                    setTakeoverInvoiceId(nextInvoiceId);
                                }
                            })
                            .catch(console.error)
                            .finally(closeModal);
                    }}
                />
            );
        },
        [closeModal, deleteInvoice, distribution?.invoices, onClose, onOpenInvoiceDeleted, openInvoiceId, openModal]
    );

    const headCells = [
        { id: 'invoice', className: SIMPLE_TABLE_HEAD_CLASS },
        { id: 'payer', className: SIMPLE_TABLE_HEAD_CLASS },
        { id: 'status', className: SIMPLE_TABLE_HEAD_CLASS },
        { id: 'share-rate', className: NUMERIC_HEAD_CLASS },
        { id: 'share-of-net', className: NUMERIC_HEAD_CLASS, values: { currency } },
        { id: 'payable', className: NUMERIC_HEAD_CLASS, values: { currency } },
        ...(isEditable ? [{ id: 'actions', className: cn(SIMPLE_TABLE_HEAD_CLASS, 'w-16') }] : []),
    ];

    const footer = isEditable ? (
        <ModalFooter
            kit
            onCancel={onClose}
            onSave={handleSave}
            disableSave={!isTotalComplete || isSaving || isDeleting || rows.length === 0}
        />
    ) : (
        <ModalFooterSimple
            kit
            buttons={[
                <Button key="close" {...buttonStyle({ variant: 'outlined', color: 'primary' })} onClick={onClose}>
                    {t('close')}
                </Button>,
            ]}
        />
    );

    const distributionDescription = distribution ? (
        <FormattedMessage
            id="invoices.payment-distribution.description"
            values={{
                count: distribution.invoices.length,
                amount: <MoneyDisplay amount={net} currency={currency} />,
                caseCount: matterNames.length,
                matters: isNew ? (
                    matterNames.join(', ')
                ) : (
                    <span className="text-fg font-semibold">{matterNames.join(', ')}</span>
                ),
            }}
        />
    ) : undefined;

    return (
        <>
            <ModalHeader
                kit
                title={t('invoices.payment-distribution.title')}
                description={isGroupDissolved ? undefined : distributionDescription}
                onCancel={onClose}
            />
            <div className="flex h-full flex-col gap-4 overflow-y-auto px-6 pt-4 pb-3 max-sm:px-4">
                {isGroupDissolved ? (
                    <p className="text-body2 text-fg-secondary">
                        <FormattedMessage id="invoices.payment-distribution.no-longer-shared" />
                    </p>
                ) : !distribution ? (
                    <Loader />
                ) : (
                    <>
                        {/* The new design says this in the header's description. */}
                        <p className="text-body2 text-fg-secondary new:hidden">{distributionDescription}</p>
                        {isLocked ? (
                            <Alert>
                                <LockIcon className="size-4" />
                                <AlertTitle>
                                    <FormattedMessage
                                        id="invoices.payment-distribution.locked-title"
                                        values={{ number: distribution.lockedByInvoiceNumber ?? '' }}
                                    />
                                </AlertTitle>
                                <AlertDescription>
                                    <FormattedMessage id="invoices.payment-distribution.locked-description" />
                                    {isStoredTotalComplete ? null : (
                                        <>
                                            {' '}
                                            <FormattedMessage
                                                id="invoices.payment-distribution.locked-incomplete-description"
                                                values={{ total: formatShareRate(storedTotalRate) }}
                                            />
                                        </>
                                    )}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {!isLocked && !hasManagePermission ? (
                            <p className="text-caption text-fg-secondary flex items-center gap-1.5">
                                <InfoOutlined className="size-4 shrink-0" />
                                <FormattedMessage id="invoices.payment-distribution.read-only-info" />
                            </p>
                        ) : null}
                        {showIncompleteAlert ? (
                            <Alert variant="destructive">
                                <CircleAlert className="size-4" />
                                {deletedInvoice ? (
                                    <>
                                        <AlertTitle>
                                            <FormattedMessage
                                                id="invoices.payment-distribution.incomplete-title"
                                                values={{
                                                    number: deletedInvoice.invoiceNumber,
                                                    total: formatShareRate(totalRate),
                                                }}
                                            />
                                        </AlertTitle>
                                        <AlertDescription>
                                            <FormattedMessage
                                                id="invoices.payment-distribution.incomplete-description"
                                                values={{ rate: formatShareRate(deletedInvoice.shareRate) }}
                                            />
                                        </AlertDescription>
                                    </>
                                ) : (
                                    <>
                                        <AlertTitle>
                                            <FormattedMessage
                                                id="invoices.payment-distribution.shares-incomplete-title"
                                                values={{ total: formatShareRate(totalRate) }}
                                            />
                                        </AlertTitle>
                                        <AlertDescription>
                                            <FormattedMessage id="invoices.payment-distribution.shares-incomplete-description" />
                                        </AlertDescription>
                                    </>
                                )}
                            </Alert>
                        ) : null}
                        <div className={PAYER_TABLE_FRAME_CLASS}>
                            <table className={PAYER_TABLE_CLASS}>
                                <TableHeader className="[&_tr]:border-b-0">
                                    <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                        {headCells.map((cell) => (
                                            <TableHead key={cell.id} className={cell.className}>
                                                {/* The new design's delete column shows no heading; it is still named. */}
                                                <span
                                                    className={isNew && cell.id === 'actions' ? 'sr-only' : undefined}
                                                >
                                                    <FormattedMessage
                                                        id={`invoices.payment-distribution.columns.${cell.id}`}
                                                        values={cell.values}
                                                    />
                                                </span>
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {fields.map((field, index) => {
                                        const invoice = invoicesById.get(field.invoiceId);
                                        if (!invoice) {
                                            return null;
                                        }
                                        const isOpenInvoice = invoice.id === openInvoiceId;
                                        return (
                                            <TableRow
                                                key={field.formKey}
                                                className={
                                                    isOpenInvoice ? CURRENT_INVOICE_ROW_CLASS : SIMPLE_TABLE_ROW_CLASS
                                                }
                                                aria-current={isOpenInvoice ? 'page' : undefined}
                                            >
                                                <TableCell className={SIMPLE_TABLE_CELL_CLASS}>
                                                    {isOpenInvoice ? (
                                                        <div className="flex flex-col">
                                                            <span className="font-semibold">
                                                                {invoice.invoiceNumber}
                                                            </span>
                                                            <span className="text-caption text-fg-secondary">
                                                                <FormattedMessage id="invoices.payment-distribution.this-invoice" />
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <InvoiceLink
                                                            invoiceId={invoice.id}
                                                            invoiceNumber={invoice.invoiceNumber}
                                                            onClick={onClose}
                                                        />
                                                    )}
                                                </TableCell>
                                                <PayerCell
                                                    payer={invoice.payer}
                                                    className={cn(SIMPLE_TABLE_CELL_CLASS, READ_ONLY_LINK_CELL_CLASS)}
                                                    onNavigate={onClose}
                                                />
                                                <TableCell className={SIMPLE_TABLE_CELL_CLASS}>
                                                    <StatusChip
                                                        type={getInvoiceStatusChipType(invoice.status)}
                                                        label={getClassifierText(
                                                            classifiers['INVOICE_STATUS_TYPE'],
                                                            invoice.status
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell className={cn(NUMERIC_CELL_CLASS, 'w-[140px]')}>
                                                    {isEditable ? (
                                                        <ShareInput
                                                            control={control}
                                                            name={`shares.${index}.shareRate`}
                                                            kind="rate"
                                                            className="w-[120px]"
                                                            ariaLabel={t(
                                                                'invoices.payment-distribution.columns.share-rate'
                                                            )}
                                                            // The new design marks the Total for a short sum, and a share only when it is out of range itself.
                                                            isInvalid={
                                                                isNew
                                                                    ? !isShareRateInRange(rates[index])
                                                                    : !isTotalComplete
                                                            }
                                                            onValueChange={(value) => handleRateChange(index, value)}
                                                        />
                                                    ) : (
                                                        <ShareRateDisplay value={shareValueToNumber(rates[index])} />
                                                    )}
                                                </TableCell>
                                                <TableCell className={cn(NUMERIC_CELL_CLASS, 'w-[160px]')}>
                                                    {isEditable ? (
                                                        <ShareInput
                                                            control={control}
                                                            name={`shares.${index}.shareAmount`}
                                                            kind="amount"
                                                            className="w-[140px]"
                                                            ariaLabel={t(
                                                                'invoices.payment-distribution.columns.share-of-net',
                                                                { currency }
                                                            )}
                                                            onValueChange={(value) => handleAmountChange(index, value)}
                                                        />
                                                    ) : (
                                                        <MoneyDisplay amount={amounts[index]} />
                                                    )}
                                                </TableCell>
                                                <TableCell className={NUMERIC_CELL_CLASS}>
                                                    {payables[index] === null ? (
                                                        '—'
                                                    ) : (
                                                        <MoneyDisplay amount={payables[index]} />
                                                    )}
                                                </TableCell>
                                                {isEditable ? (
                                                    <TableCell className={ACTIONS_CELL_CLASS}>
                                                        {invoice.isDeletable ? (
                                                            <IconButton
                                                                size="small"
                                                                className="new:text-foreground-secondary"
                                                                aria-label={t(
                                                                    'invoices.payment-distribution.delete-invoice',
                                                                    { number: invoice.invoiceNumber }
                                                                )}
                                                                disabled={isDeleting}
                                                                onClick={() => handleDelete(invoice)}
                                                            >
                                                                <Trash2Icon className="size-4" />
                                                            </IconButton>
                                                        ) : null}
                                                    </TableCell>
                                                ) : null}
                                            </TableRow>
                                        );
                                    })}
                                    {creditedInvoices.map((invoice) => (
                                        <CreditedInvoiceRow
                                            key={invoice.id}
                                            invoice={invoice}
                                            hasActionsColumn={isEditable}
                                            onNavigate={onClose}
                                        />
                                    ))}
                                </TableBody>
                                <TableFooter className="border-t-0 bg-transparent">
                                    <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                        <TableCell className={FOOTER_CELL_CLASS} colSpan={3}>
                                            <FormattedMessage id="invoices.payment-distribution.total" />
                                        </TableCell>
                                        <TableCell className={cn(FOOTER_CELL_CLASS, 'text-right')}>
                                            <ShareTotalIndicator total={totalRate} isComplete={isTotalComplete} />
                                        </TableCell>
                                        <TableCell
                                            className={cn(
                                                FOOTER_CELL_CLASS,
                                                'text-right',
                                                !isTotalComplete && 'text-error'
                                            )}
                                        >
                                            {isTotalComplete ? (
                                                <MoneyDisplay amount={totalAmount} />
                                            ) : (
                                                <FormattedMessage
                                                    id="invoices.payment-distribution.of-total"
                                                    values={{
                                                        amount: <MoneyDisplay amount={totalAmount} />,
                                                        total: <MoneyDisplay amount={net} />,
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell className={cn(FOOTER_CELL_CLASS, 'text-right')}>
                                            {totalPayable === null ? '—' : <MoneyDisplay amount={totalPayable} />}
                                        </TableCell>
                                        {isEditable ? <TableCell className={FOOTER_CELL_CLASS} /> : null}
                                    </TableRow>
                                </TableFooter>
                            </table>
                        </div>
                        {isEditable ? (
                            <div className="-mt-3 flex flex-col gap-0.5">
                                {!showIncompleteAlert && !isTotalComplete ? (
                                    <p className="text-caption text-error" role="alert">
                                        <FormattedMessage
                                            id="invoices.payment-distribution.total-mismatch"
                                            values={{ total: formatShareRate(totalRate) }}
                                        />
                                    </p>
                                ) : null}
                                <p className="text-caption text-fg-secondary">
                                    <FormattedMessage id="invoices.payment-distribution.footnote" />
                                </p>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
            {footer}
        </>
    );
}

function InvoiceLink({
    invoiceId,
    invoiceNumber,
    onClick,
}: {
    invoiceId: number;
    invoiceNumber: string;
    onClick: () => void;
}) {
    return <RecordLink to={`/invoice/${invoiceId}`} className={INVOICE_LINK} onClick={onClick} label={invoiceNumber} />;
}

function isPlainLinkClick(event: MouseEvent<HTMLElement>): boolean {
    const isOnLink = event.target instanceof Element && event.target.closest('a') !== null;
    return isOnLink && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/** The dialog is not tied to the route, so it has to close itself when the payer's link leaves the page. */
function PayerCell({
    payer,
    className,
    onNavigate,
}: {
    payer: PaymentDistributionInvoiceDto['payer'];
    className: string;
    onNavigate: () => void;
}) {
    return (
        <TableCell
            className={className}
            onClick={(event) => {
                if (isPlainLinkClick(event)) {
                    onNavigate();
                }
            }}
        >
            <PartyName party={payer} isLink useLinkWithOrigin />
        </TableCell>
    );
}

/** A credited copy: no longer part of the distribution, listed so the invoice a lock names can be found. */
function CreditedInvoiceRow({
    invoice,
    hasActionsColumn,
    onNavigate,
}: {
    invoice: PaymentDistributionCreditedInvoiceDto;
    hasActionsColumn: boolean;
    onNavigate: () => void;
}) {
    return (
        <TableRow className={CREDITED_ROW_CLASS}>
            <TableCell className={SIMPLE_TABLE_CELL_CLASS}>
                <InvoiceLink invoiceId={invoice.id} invoiceNumber={invoice.invoiceNumber} onClick={onNavigate} />
            </TableCell>
            <PayerCell payer={invoice.payer} className={SIMPLE_TABLE_CELL_CLASS} onNavigate={onNavigate} />
            <TableCell className={SIMPLE_TABLE_CELL_CLASS}>
                <StatusChip
                    type={getInvoiceStatusChipType(InvoiceStatusType.CREDITED)}
                    label={<FormattedMessage id="invoices.payment-distribution.credited" />}
                />
            </TableCell>
            <TableCell className={NUMERIC_CELL_CLASS}>
                <ShareRateDisplay value={invoice.shareRate} />
            </TableCell>
            <TableCell className={NUMERIC_CELL_CLASS}>
                <MoneyDisplay amount={invoice.shareAmount} />
            </TableCell>
            <TableCell className={NUMERIC_CELL_CLASS} />
            {hasActionsColumn ? <TableCell className={ACTIONS_CELL_CLASS} /> : null}
        </TableRow>
    );
}
